import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Bell, Megaphone } from "lucide-react";

const timeAgo = (iso) => {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const NotificationBell = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const fetchItems = () => api.get("/notifications").then((r) => { setItems(r.data.items); setUnread(r.data.unread); }).catch(() => {});

  useEffect(() => {
    fetchItems();
    const t = setInterval(fetchItems, 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && unread > 0) {
      try { await api.post("/notifications/read"); setUnread(0); setItems((prev) => prev.map((n) => ({ ...n, read: true }))); } catch { /* ignore */ }
    }
  };

  const go = (n) => { setOpen(false); if (n.link) navigate(n.link); };

  return (
    <div className="relative" ref={ref}>
      <button onClick={toggle} data-testid="notif-bell"
        className="relative w-9 h-9 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors">
        <Bell className="w-[18px] h-[18px]" />
        {unread > 0 && <span data-testid="notif-unread-dot" className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-secondary text-white text-[10px] font-bold flex items-center justify-center">{unread > 9 ? "9+" : unread}</span>}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-80 max-h-96 overflow-y-auto rounded-xl border border-border bg-popover shadow-2xl z-50" data-testid="notif-panel">
          <div className="px-4 py-3 border-b border-border/60 text-sm font-semibold">Notifications</div>
          {items.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">You're all caught up ✨</div>
          ) : (
            <ul className="divide-y divide-border/40">
              {items.map((n) => (
                <li key={n.id}>
                  <button onClick={() => go(n)} data-testid={`notif-item-${n.id}`}
                    className={`w-full text-left px-4 py-3 flex gap-3 hover:bg-white/5 transition-colors ${!n.read ? "bg-primary/[0.04]" : ""}`}>
                    <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0"><Megaphone className="w-4 h-4" /></div>
                    <div className="min-w-0">
                      <div className="text-sm font-medium leading-snug">{n.title}</div>
                      {n.body && <div className="text-xs text-muted-foreground truncate mt-0.5">{n.body}</div>}
                      <div className="text-[11px] text-muted-foreground mt-1">{timeAgo(n.created_at)}</div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
